import { ContextValue, datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";

import { env } from "~/env";

type DatadogSite =
  | "datadoghq.com"
  | "datadoghq.eu"
  | "us3.datadoghq.com"
  | "us5.datadoghq.com"
  | "ddog-gov.com";

/**
 * Initialize DataDog RUM (Real User Monitoring)
 * This should be called once at the application startup
 */
export function initializeDatadogRUM() {
  if (!env.VITE_DATADOG_ENABLED) {
    return;
  }

  // Disable RUM in FedRAMP environments
  if (env.VITE_IS_FEDRAMP) {
    return;
  }

  // Only initialize if we have the required configuration
  if (!env.VITE_DATADOG_APPLICATION_ID || !env.VITE_DATADOG_CLIENT_TOKEN) {
    console.warn(
      "DataDog RUM not initialized: Missing VITE_DATADOG_APPLICATION_ID or VITE_DATADOG_CLIENT_TOKEN",
    );
    return;
  }
  // Don't initialize in test environment
  if (env.VITE_NODE_ENV === "test") {
    return;
  }

  datadogRum.init({
    applicationId: env.VITE_DATADOG_APPLICATION_ID,
    clientToken: env.VITE_DATADOG_CLIENT_TOKEN,
    site: env.VITE_DATADOG_SITE as DatadogSite,
    service: env.VITE_DATADOG_SERVICE_NAME,
    env: env.VITE_DATADOG_ENV,

    // Session configuration
    sessionSampleRate: env.VITE_DATADOG_SAMPLING_RATE,
    sessionReplaySampleRate: env.VITE_DATADOG_SESSION_REPLAY_SAMPLING_RATE,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,

    // Default privacy settings
    defaultPrivacyLevel: "mask-user-input",

    // Enable useful features
    trackViewsManually: false, // Let DataDog handle view tracking automatically
    enableExperimentalFeatures: ["clickmap"],

    // Enable verbose logging for debugging
    silentMultipleInit: false,

    // Custom configuration for React Router v7
    beforeSend: (_event, _context) => {
      // You can modify or filter events before they're sent
      // For example, you might want to sanitize URLs or add custom context
      // Return true to send the event, false to drop it
      return true;
    },

    plugins: [reactPlugin({ router: true })],
  });

  // Start collecting RUM data immediately
  datadogRum.startSessionReplayRecording();
}

/**
 * Add custom user context to DataDog RUM
 * Call this when user authentication state changes
 */
export function setDatadogUser(user: {
  id?: string;
  name?: string;
  email?: string;
  [key: string]: string | number | boolean | undefined;
}) {
  if (datadogRum.getInitConfiguration()) {
    datadogRum.setUser(user);
  }
}

/**
 * Add custom global context to DataDog RUM
 */
export function addDatadogGlobalContext(key: string, value: ContextValue) {
  if (datadogRum.getInitConfiguration()) {
    datadogRum.setGlobalContext({ [key]: value });
  }
}

/**
 * Track a custom user action
 */
export function trackDatadogAction(
  name: string,
  context?: Record<string, string | number | boolean>,
) {
  if (datadogRum.getInitConfiguration()) {
    datadogRum.addAction(name, context);
  }
}

/**
 * Add custom error tracking
 */
export function trackDatadogError(
  error: Error,
  context?: Record<string, string | number | boolean>,
) {
  if (datadogRum.getInitConfiguration()) {
    datadogRum.addError(error, context);
  }
}

/**
 * Add custom timing for performance monitoring
 */
export function trackDatadogTiming(name: string, duration: number) {
  if (datadogRum.getInitConfiguration()) {
    datadogRum.addTiming(name, duration);
  }
}
